import React, { useState } from 'react';
import { Mail, Lock, User, ArrowRight, AlertCircle, Loader2, CheckCircle } from 'lucide-react';
import { 
  registerWithEmail, 
  signInWithEmail, 
  signInWithGoogle,
  resetPassword,
  linkGoogleAccount
} from '../services/authService';
import { User as UserType } from '../types';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      reset: (widgetId?: number) => void;
    };
  }
}

interface AuthFormProps {
  onLogin: (user: UserType) => void;
}

type AuthMode = 'login' | 'register' | 'reset';

// Get reCAPTCHA v3 site key from environment variable
// This is different from VITE_RECAPTCHA_SITE_KEY (used for App Check)
// Configure in .env.local: VITE_RECAPTCHA_V3_SITE_KEY=sua_chave_aqui
const RECAPTCHA_SITE_KEY = (import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || '').trim();

const AuthForm: React.FC<AuthFormProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingGoogleLink, setPendingGoogleLink] = useState(false);

  const validateEmail = (email: string) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  const getRecaptchaToken = async (): Promise<string> => {
    // If reCAPTCHA is not configured, return empty string (will be handled by backend)
    // reCAPTCHA v3 is optional - authentication works without it
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY === '') {
      return '';
    }

    return new Promise((resolve, reject) => {
      // Wait for grecaptcha to be available (script might still be loading)
      const maxAttempts = 50; // 5 seconds max wait
      let attempts = 0;
      
      const checkRecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.ready) {
          window.grecaptcha.ready(async () => {
            try {
              const action = mode === 'register' ? 'register' : 'login';
              const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
              resolve(token);
            } catch (error: any) {
              // Handle domain errors gracefully
              if (error?.message?.includes('Invalid domain') || error?.message?.includes('domínio inválido')) {
                console.error('❌ Erro: Domínio não autorizado no reCAPTCHA. Adicione seu domínio em https://www.google.com/recaptcha/admin');
                reject(new Error('Domínio não autorizado no reCAPTCHA'));
              } else {
                reject(error);
              }
            }
          });
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkRecaptcha, 100);
        } else {
          reject(new Error('reCAPTCHA não carregado. Verifique se a chave está correta e o domínio está autorizado.'));
        }
      };
      
      checkRecaptcha();
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateEmail(email)) {
      setError('Por favor, insira um e-mail válido.');
      return;
    }

    if (mode === 'reset') {
      setLoading(true);
      try {
        await resetPassword(email);
        setSuccess('E-mail de recuperação enviado! Verifique sua caixa de entrada.');
        setTimeout(() => setMode('login'), 3000);
      } catch (err: unknown) {
        const error = err as { code?: string };
        if (error.code === 'auth/user-not-found') {
          setError('E-mail não encontrado.');
        } else {
          setError('Erro ao enviar e-mail de recuperação.');
        }
      } finally {
        setLoading(false);
      }
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (mode === 'register' && !name.trim()) {
      setError('Por favor, informe seu nome.');
      return;
    }

    setLoading(true);

    try {
      // Obter token do reCAPTCHA
      let recaptchaToken: string = '';
      try {
        recaptchaToken = await getRecaptchaToken();
      } catch (recaptchaError: any) {
        const errorMsg = recaptchaError?.message || 'Erro ao verificar reCAPTCHA';
        console.error('Erro ao obter token reCAPTCHA:', recaptchaError);
        
        // Se for erro de domínio, mostrar mensagem específica
        if (errorMsg.includes('Domínio não autorizado')) {
          setError('Domínio não autorizado no reCAPTCHA. Adicione seu domínio em https://www.google.com/recaptcha/admin');
        } else if (errorMsg.includes('não carregado')) {
          setError('reCAPTCHA não carregado. Recarregue a página e tente novamente.');
        } else {
          setError('Erro ao verificar reCAPTCHA. Por favor, recarregue a página e tente novamente.');
        }
        setLoading(false);
        return;
      }

      let user: UserType;
      
      if (mode === 'register') {
        user = await registerWithEmail(email, password, name, recaptchaToken);
        setSuccess('Conta criada! Verifique seu e-mail para confirmar.');
      } else {
        user = await signInWithEmail(email, password, recaptchaToken);
        
        // If user was trying to link Google account, do it now
        if (pendingGoogleLink) {
          try {
            setSuccess('Vinculando conta do Google...');
            user = await linkGoogleAccount();
            setSuccess('Conta do Google vinculada com sucesso!');
            setPendingGoogleLink(false);
          } catch (linkError: any) {
            const linkErr = linkError as { code?: string; message?: string };
            if (linkErr.code === 'auth/popup-closed-by-user') {
              setSuccess('Login realizado. Você pode vincular sua conta do Google depois.');
              setPendingGoogleLink(false);
            } else {
              setError(linkErr.message || 'Erro ao vincular conta do Google.');
              setPendingGoogleLink(false);
            }
          }
        }
      }
      
      onLogin(user);
    } catch (err: unknown) {
      const error = err as { code?: string };
      
      // Handle Firebase Auth errors
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Este e-mail já está cadastrado.');
          break;
        case 'auth/invalid-email':
          setError('E-mail inválido.');
          break;
        case 'auth/weak-password':
          setError('Senha muito fraca. Use pelo menos 6 caracteres.');
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('E-mail ou senha incorretos.');
          break;
        case 'auth/too-many-requests':
          setError('Muitas tentativas. Aguarde alguns minutos.');
          break;
        default:
          setError('Erro ao autenticar. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const user = await signInWithGoogle();
      onLogin(user);
    } catch (err: unknown) {
      const error = err as { code?: string; email?: string; message?: string };
      
      if (error.code === 'auth/popup-closed-by-user') {
        // User closed popup, not an error
        setLoading(false);
        return;
      } else if (error.code === 'auth/popup-blocked') {
        setError('Pop-up bloqueado. Permita pop-ups para este site.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        // Account exists with email/password, need to link
        const existingEmail = error.email;
        if (existingEmail) {
          setEmail(existingEmail);
          setPendingGoogleLink(true);
          setError('Este email já está cadastrado com email e senha. Por favor, faça login para vincular sua conta do Google.');
          setMode('login');
        } else {
          setError('Este email já está cadastrado com outro método de login. Faça login com email e senha primeiro.');
        }
      } else {
        setError(error.message || 'Erro ao entrar com Google. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">
          {mode === 'register' ? 'Criar Conta' : mode === 'reset' ? 'Recuperar Senha' : 'Acesse sua Conta'}
        </h2>
        <p className="text-slate-400 text-sm">
          {mode === 'register' 
            ? 'Comece a consultar placas hoje mesmo.' 
            : mode === 'reset'
            ? 'Enviaremos um link para redefinir sua senha.'
            : 'Bem-vindo de volta ao Pedigree do Carro.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        
        {mode === 'register' && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-400 ml-1">Nome Completo</label>
            <div className="relative">
              <User className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
              <input 
                type="text" 
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                placeholder="Seu nome"
                disabled={loading}
              />
            </div>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs font-medium text-slate-400 ml-1">E-mail</label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
              placeholder="seu@email.com"
              disabled={loading}
            />
          </div>
        </div>

        {mode !== 'reset' && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-slate-400 ml-1">Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
              <input 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2.5 pl-10 pr-4 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all"
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-400/10 p-3 rounded-lg border border-red-400/20">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-400/10 p-3 rounded-lg border border-emerald-400/20">
            <CheckCircle className="w-4 h-4 shrink-0" />
            {success}
          </div>
        )}

        <button 
          type="submit"
          disabled={loading}
          className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-600/50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 group"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processando...
            </>
          ) : (
            <>
              {mode === 'register' ? 'Cadastrar' : mode === 'reset' ? 'Enviar E-mail' : 'Entrar'}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>

        {mode === 'login' && (
          <button 
            type="button"
            onClick={() => { 
              setMode('reset'); 
              setError(''); 
              setSuccess('');
              setPendingGoogleLink(false);
            }}
            className="w-full text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            Esqueci minha senha
          </button>
        )}

        {mode !== 'reset' && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-900 text-slate-500">Ou continue com</span>
              </div>
            </div>

            <button 
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="w-full bg-white text-slate-900 font-medium py-2.5 rounded-lg transition-all hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>
          </>
        )}
      </form>

      <div className="mt-6 text-center">
        {mode === 'reset' ? (
          <button 
            onClick={() => { 
              setMode('login'); 
              setError(''); 
              setSuccess('');
              setPendingGoogleLink(false);
            }}
            className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            Voltar para login
          </button>
        ) : (
          <button 
            onClick={() => { 
              setMode(mode === 'register' ? 'login' : 'register'); 
              setError(''); 
              setSuccess('');
              setPendingGoogleLink(false);
            }}
            className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
          >
            {mode === 'register' ? 'Já tem uma conta? Faça login' : 'Não tem conta? Cadastre-se grátis'}
          </button>
        )}
      </div>
    </div>
  );
};

export default AuthForm;
